import { type FederalDistrictItem } from '@entities/federal-district'
import { toTelegramHref, type FulfillmentApi, type OwnerFulfillment } from '@entities/fulfillment'

import { type FulfillmentFormValues, type MarketplaceForm } from '../model/types'
import { rublesToKopecks } from './rubles'
import { buildSaveServicePrices, toServicePricesForm } from './service-prices-form'

const emptyArea = () => ({ mode: 'selected' as const, cities: [] })

const kopecksToRubles = (value: number | null) => (value === null ? '' : (value / 100).toFixed(2).replace(/\.00$/, ''))

const hasDestinationCode = (destination: FulfillmentApi.Destination): destination is FulfillmentApi.SaveDestination =>
  Boolean(destination.code?.trim())

type SavedMarketplaceForm = Omit<MarketplaceForm, 'destinations'> & {
  destinations?: FulfillmentApi.Destination[]
  destinationNames?: string
}

type SavedFulfillmentFormValues = Omit<Partial<FulfillmentFormValues>, 'marketplaces'> & {
  marketplaces?: SavedMarketplaceForm[]
}

const normalizeMarketplaceDestinations = (
  item: SavedMarketplaceForm,
  knownDistrictCodes?: ReadonlySet<string>
): MarketplaceForm => {
  const { destinationNames, ...current } = item
  const savedDestinations = Array.isArray(item.destinations) ? item.destinations : []
  const destinations = savedDestinations
    .filter(hasDestinationCode)
    .filter((destination) => !knownDistrictCodes || knownDistrictCodes.has(destination.code))
  const hasLegacyDestinationNames = Boolean(destinationNames && destinationNames.trim())

  return {
    ...current,
    destinations,
    needsDistrictReselection:
      item.needsDistrictReselection || hasLegacyDestinationNames || destinations.length !== savedDestinations.length,
  }
}

/**
 * Убирает из значений формы округа, которых нет в справочнике, и помечает такие площадки как
 * требующие повторного выбора. Нужно для карточек, сохранённых до перехода на округа: там в
 * направлениях лежат произвольные названия вроде «МО и ЦФО», которые бэкенд больше не примет.
 */
export const normalizeFulfillmentFormDistricts = (
  values: FulfillmentFormValues,
  federalDistricts: readonly FederalDistrictItem[]
): FulfillmentFormValues => {
  const knownDistrictCodes = new Set(federalDistricts.map((district) => district.code))

  return {
    ...values,
    marketplaces: values.marketplaces.map((item) => normalizeMarketplaceDestinations(item, knownDistrictCodes)),
  }
}

export const emptyFulfillmentFormValues: FulfillmentFormValues = {
  name: '',
  inn: '',
  description: '',
  shipmentTypes: ['fbo'],
  locations: [{ city: null, address: '' }],
  fbsArea: emptyArea(),
  dbsArea: emptyArea(),
  marketplaces: [],
  categoryMode: 'all',
  categoryExclusions: '',
  deliveryFrequency: '',
  otherShipmentDescription: '',
  areaSquareMeters: '',
  operationStartedYear: '',
  activeClientCount: '',
  minimumUnitPriceRubles: '',
  servicePrices: {},
  yandexMapsUrl: '',
  twoGisUrl: '',
  websiteUrl: '',
  phone: '',
  email: '',
  telegram: '',
  max: '',
}

/**
 * Значения формы из сохранённого черновика. Черновик мог быть записан прежней версией страницы, где
 * какого-то поля ещё не было, поэтому недостающее берём из пустых значений. Без этого поле осталось
 * бы неопределённым, а отправка формы упала бы на нём — черновик молча ломал бы сохранение карточки.
 */
export const draftToFulfillmentFormValues = (saved: SavedFulfillmentFormValues): FulfillmentFormValues => ({
  ...emptyFulfillmentFormValues,
  ...saved,
  // Вложенные площадки нормализуем отдельно: старый черновик мог сохранить прежние направления.
  marketplaces: (saved.marketplaces ?? emptyFulfillmentFormValues.marketplaces).map((item) =>
    normalizeMarketplaceDestinations(item)
  ),
  // Прайс берём из черновика целиком: это списки услуг по схемам работы, сливать их по ключам
  // нечего — состав приходит из справочника, а не из нашего перечня.
  servicePrices: saved.servicePrices ?? emptyFulfillmentFormValues.servicePrices,
})

const toAreaValues = (area: OwnerFulfillment['serviceAreas']['fbs']) =>
  area
    ? {
        mode: area.mode,
        cities: area.cities.map((city) => ({ id: city.id, name: city.name, regionName: city.regionName })),
      }
    : emptyArea()

const toMarketplaceValues = (item: OwnerFulfillment['marketplaces'][number]): MarketplaceForm =>
  normalizeMarketplaceDestinations({
    marketplace: item.marketplace,
    otherName: item.otherName ?? '',
    coverageMode: item.coverageMode,
    destinations: item.destinations,
  })

export const ownerToFulfillmentFormValues = (item: OwnerFulfillment): FulfillmentFormValues => ({
  name: item.name,
  inn: item.inn,
  description: item.description,
  shipmentTypes: item.shipmentTypes,
  locations: item.locations.map((location) => ({
    city: { id: location.city.id, name: location.city.name, regionName: location.city.regionName },
    address: location.address ?? '',
  })),
  fbsArea: toAreaValues(item.serviceAreas.fbs),
  dbsArea: toAreaValues(item.serviceAreas.dbs),
  marketplaces: item.marketplaces.map(toMarketplaceValues),
  categoryMode: item.categoryMode,
  categoryExclusions: item.categoryExclusions ?? '',
  deliveryFrequency: item.deliveryFrequency ?? '',
  otherShipmentDescription: item.otherShipmentDescription ?? '',
  areaSquareMeters: item.areaSquareMeters?.toString() ?? '',
  operationStartedYear: item.operationStartedYear?.toString() ?? '',
  activeClientCount: item.activeClientCount?.toString() ?? '',
  minimumUnitPriceRubles: kopecksToRubles(item.minimumUnitPrice),
  servicePrices: toServicePricesForm(item.servicePrices),
  yandexMapsUrl: item.yandexMapsUrl ?? '',
  twoGisUrl: item.twoGisUrl ?? '',
  websiteUrl: item.websiteUrl ?? '',
  phone: item.phone ?? '',
  email: item.email ?? '',
  telegram: item.telegram ?? '',
  max: item.max ?? '',
})

const nullableInteger = (value: string) => (value ? Number(value) : null)
const nullableText = (value: string) => value.trim() || null
const toTelegramContact = (value: string) => {
  const contact = nullableText(value)
  return toTelegramHref(contact) ?? contact
}

const toServiceArea = (area: FulfillmentFormValues['fbsArea']) => ({
  mode: area.mode,
  cities:
    area.mode === 'selected'
      ? area.cities.map((city) => ({ name: city.name.trim(), region_name: city.regionName.trim() }))
      : [],
})

const toMarketplace = (
  item: MarketplaceForm,
  knownDistrictCodes?: ReadonlySet<string>
): FulfillmentApi.SaveMarketplaceItem => ({
  marketplace: item.marketplace,
  other_name: item.marketplace === 'other' ? item.otherName.trim() : null,
  coverage_mode: item.coverageMode,
  destinations:
    item.coverageMode === 'all'
      ? []
      : item.destinations
          .filter(hasDestinationCode)
          .filter((destination) => !knownDistrictCodes || knownDistrictCodes.has(destination.code)),
})

export const toFulfillmentSaveRequest = (
  values: FulfillmentFormValues,
  federalDistricts?: readonly FederalDistrictItem[]
): FulfillmentApi.SaveRequest => {
  const knownDistrictCodes = federalDistricts ? new Set(federalDistricts.map((district) => district.code)) : undefined

  return {
    name: values.name.trim(),
    inn: values.inn,
    description: values.description.trim(),
    shipment_types: values.shipmentTypes,
    locations: values.locations.flatMap((location) =>
      location.city
        ? [
            {
              name: location.city.name.trim(),
              region_name: location.city.regionName.trim(),
              address: nullableText(location.address),
            },
          ]
        : []
    ),
    service_areas: {
      ...(values.shipmentTypes.includes('fbs') ? { fbs: toServiceArea(values.fbsArea) } : {}),
      ...(values.shipmentTypes.includes('dbs') ? { dbs: toServiceArea(values.dbsArea) } : {}),
    },
    marketplaces: values.shipmentTypes.includes('fbs')
      ? values.marketplaces.map((item) => toMarketplace(item, knownDistrictCodes))
      : [],
    category_mode: values.categoryMode,
    category_exclusions: values.categoryMode === 'excluding' ? nullableText(values.categoryExclusions) : null,
    delivery_frequency: values.deliveryFrequency || null,
    other_shipment_description: values.shipmentTypes.includes('other')
      ? nullableText(values.otherShipmentDescription)
      : null,
    area_square_meters: nullableInteger(values.areaSquareMeters),
    operation_started_year: nullableInteger(values.operationStartedYear),
    active_client_count: nullableInteger(values.activeClientCount),
    minimum_unit_price: rublesToKopecks(values.minimumUnitPriceRubles),
    service_prices: buildSaveServicePrices(values.servicePrices, values.shipmentTypes),
    yandex_maps_url: nullableText(values.yandexMapsUrl),
    two_gis_url: nullableText(values.twoGisUrl),
    website_url: nullableText(values.websiteUrl),
    phone: nullableText(values.phone),
    email: nullableText(values.email),
    telegram: toTelegramContact(values.telegram),
    max: nullableText(values.max),
  }
}
